import type { AutocaptureConfig } from 'posthog-js';

export type AnalyticsEnvironment =
  | 'production'
  | 'sandbox'
  | 'development'
  | 'test';

type ResolvePostHogRuntimeInput = {
  hostname: string;
  mode: string;
  configuredEnvironment?: string;
  captureLocal?: string;
};

type PostHogEventLike = {
  properties?: Record<string, unknown>;
};

export function getSelectiveAutocaptureConfig(): AutocaptureConfig {
  return {
    dom_event_allowlist: ['click', 'change', 'submit'],
    css_selector_allowlist: ['[data-ph-capture]'],
    element_attribute_ignorelist: [
      'aria-label',
      'name',
      'placeholder',
      'value',
    ],
  };
}

export function preparePostHogEvent<TEvent extends PostHogEventLike>(
  event: TEvent | null,
  runtime: ReturnType<typeof resolvePostHogRuntime>,
): TEvent | null {
  if (!runtime.enabled || event === null) return null;
  const properties = { ...event.properties };
  delete properties.$el_text;
  delete properties.$elements;
  delete properties.$elements_chain;

  for (const key of ['$current_url', '$referrer', '$initial_current_url']) {
    if (typeof properties[key] === 'string') {
      properties[key] = sanitizeAnalyticsUrl(properties[key]);
    }
  }
  if (typeof properties.$pathname === 'string') {
    properties.$pathname = normalizeAnalyticsPath(properties.$pathname);
  }

  return {
    ...event,
    properties: {
      ...properties,
      environment: runtime.environment,
    },
  };
}

export function sanitizeAnalyticsUrl(value: string) {
  try {
    const url = new URL(value);
    return `${url.origin}${normalizeAnalyticsPath(url.pathname)}`;
  } catch {
    return normalizeAnalyticsPath(value.split(/[?#]/, 1)[0]);
  }
}

export function normalizeAnalyticsPath(value: string) {
  return value.replace(
    /\/(?:[0-9a-f]{8}-[0-9a-f-]{27,}|[A-Za-z0-9_-]{16,})(?=\/|$)/gi,
    '/:id',
  );
}

export function resolvePostHogRuntime({
  hostname,
  mode,
  configuredEnvironment,
  captureLocal,
}: ResolvePostHogRuntimeInput) {
  const isLocal = isLocalHostname(hostname);
  const environment = resolveAnalyticsEnvironment(
    hostname,
    mode,
    configuredEnvironment,
  );

  return {
    enabled: !isLocal || parseBoolean(captureLocal) === true,
    environment,
  };
}

export function resolveAnalyticsEnvironment(
  hostname: string,
  mode: string,
  configuredEnvironment?: string,
): AnalyticsEnvironment {
  const configured = configuredEnvironment?.trim().toLowerCase();
  if (
    configured === 'production' ||
    configured === 'sandbox' ||
    configured === 'development' ||
    configured === 'test'
  ) {
    return configured;
  }

  if (isLocalHostname(hostname)) return 'development';
  if (hostname.toLowerCase().includes('sandbox')) return 'sandbox';
  if (mode === 'test') return 'test';
  return 'production';
}

function isLocalHostname(hostname: string) {
  return (
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname === '::1' ||
    hostname.endsWith('.localhost')
  );
}

function parseBoolean(value?: string) {
  if (!value) return undefined;
  if (['1', 'true', 'yes', 'on'].includes(value.toLowerCase())) return true;
  if (['0', 'false', 'no', 'off'].includes(value.toLowerCase())) return false;
  return undefined;
}
